import { useRef, useCallback, useState, useEffect } from 'react';

// ─── useJobPolling ──────────────────────────────────────────────

export interface UseJobPollingConfig<T> {
  /** 진행률 조회 API */
  fetchProgress: (jobId: string) => Promise<{ data: T }>;
  /** Job 삭제 API (완료/실패 시 자동 호출) */
  deleteJob?: (jobId: string) => Promise<unknown>;
  /** 완료 판정 */
  isComplete: (data: T) => boolean;
  /** 실패 판정 */
  isFailed: (data: T) => boolean;
  /** 진행 중 콜백 (매 tick) */
  onProgress: (data: T) => void;
  /** 완료 시 콜백 (async 지원) */
  onComplete: (data: T) => void | Promise<void>;
  /** 실패 시 콜백 */
  onFailed: (data: T) => void;
  /** 폴링 간격 (기본 1000ms) */
  interval?: number;
}

export interface UseJobPollingReturn {
  /** 폴링 시작 (jobId) */
  start: (jobId: string) => void;
  /** 폴링 중단 */
  stop: () => void;
  /** 현재 폴링 중 여부 */
  isActive: boolean;
}

export function useJobPolling<T>(config: UseJobPollingConfig<T>): UseJobPollingReturn {
  const configRef = useRef(config);
  configRef.current = config;

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const [isActive, setIsActive] = useState(false);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    jobIdRef.current = null;
    setIsActive(false);
  }, []);

  const start = useCallback((jobId: string) => {
    // 중복 start 방지: 기존 interval 먼저 clear
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    jobIdRef.current = jobId;
    setIsActive(true);

    const tick = async () => {
      const currentJobId = jobIdRef.current;
      if (!currentJobId) return;

      try {
        const res = await configRef.current.fetchProgress(currentJobId);
        const data = res.data;
        if (!data) return;

        if (configRef.current.isComplete(data)) {
          // stop 먼저 → interval clear
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsActive(false);

          await configRef.current.onComplete(data);
          configRef.current.deleteJob?.(currentJobId).catch(() => {});
          jobIdRef.current = null;
          return;
        }

        if (configRef.current.isFailed(data)) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          setIsActive(false);

          configRef.current.onFailed(data);
          configRef.current.deleteJob?.(currentJobId).catch(() => {});
          jobIdRef.current = null;
          return;
        }

        configRef.current.onProgress(data);
      } catch {
        // 네트워크 에러 → 다음 tick 대기
      }
    };

    intervalRef.current = setInterval(tick, configRef.current.interval ?? 1000);
  }, []);

  // 컴포넌트 언마운트 시 자동 cleanup
  useEffect(() => stop, [stop]);

  return { start, stop, isActive };
}

// ─── runJobRecovery ─────────────────────────────────────────────

export interface RecoveryCheck {
  /** 디버깅용 이름 */
  name: string;
  /** active job 조회 API */
  fetchActive: () => Promise<{ data: any }>;
  /** processing 상태 판정 */
  isProcessing: (data: any) => boolean;
  /** processing 복구 시 실행할 동작 */
  onProcessing: (data: any) => void;
  /** failed 상태 판정 (서버 재시작 등으로 중단된 Job, 선택) */
  isFailed?: (data: any) => boolean;
  /** failed 복구 시 실행할 동작 (토스트 알림 + 삭제, 선택) */
  onFailed?: (data: any) => void | Promise<void>;
  /** completed 상태 판정 (Phase 2 fallback용, 선택) */
  isCompleted?: (data: any) => boolean;
  /** completed fallback 시 실행할 동작 (선택) */
  onCompleted?: (data: any) => void | Promise<void>;
}

/**
 * 페이지 로드 시 진행 중인 Job을 복구합니다.
 * Phase 1: processing 작업 우선 복구 (첫 번째 match → return true)
 * Phase 1.5: failed 작업 알림 (서버 재시작 등으로 중단된 Job → 토스트 + 삭제)
 * Phase 2: completed 작업 fallback 표시 (첫 번째 match → return true)
 */
export async function runJobRecovery(checks: RecoveryCheck[]): Promise<boolean> {
  // 모든 fetchActive를 병렬 호출
  const results = await Promise.allSettled(
    checks.map((c) => c.fetchActive()),
  );

  // Phase 1: processing 작업 탐색 (우선순위 = 배열 순서)
  for (let i = 0; i < checks.length; i++) {
    const result = results[i];
    if (result.status !== 'fulfilled') continue;

    const data = result.value.data;
    if (data && checks[i].isProcessing(data)) {
      checks[i].onProcessing(data);
      return true;
    }
  }

  // Phase 1.5: failed 작업 알림 (서버 재시작으로 중단된 Job)
  for (let i = 0; i < checks.length; i++) {
    const result = results[i];
    if (result.status !== 'fulfilled') continue;

    const check = checks[i];
    const data = result.value.data;
    if (data && check.isFailed?.(data) && check.onFailed) {
      await check.onFailed(data);
      return true;
    }
  }

  // Phase 2: completed fallback (배열 순서대로)
  for (let i = 0; i < checks.length; i++) {
    const result = results[i];
    if (result.status !== 'fulfilled') continue;

    const check = checks[i];
    const data = result.value.data;
    if (data && check.isCompleted?.(data) && check.onCompleted) {
      await check.onCompleted(data);
      return true;
    }
  }

  return false;
}
